import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp, doc, onSnapshot, getDocs } from 'firebase/firestore';

/**
 * Service for communicating with the Desktop Agent.
 */
export const DesktopService = {

    /**
     * Fetches all registered devices for a user.
     * @param {string} userId - The user's UID
     * @returns {Promise<Array>} - Array of device objects
     */
    getUserDevices: async (userId) => {
        try {
            if (!userId) return [];
            const devicesRef = collection(db, 'users', userId, 'devices');
            const snapshot = await getDocs(devicesRef);
            return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (err) {
            console.error("Error fetching devices:", err);
            return [];
        }
    },

    /**
     * Sends a command to a specific user's desktop device queue.
     * @param {string} userId - The user's UID (Target device owner)
     * @param {string} deviceId - The UUID of the target computer
     * @param {string|Object} commandPayload - The command string or object
     * @param {string} chatId - The ID of the chat where the request originated
     * @returns {Promise<string>} - The ID of the created task
     */
    sendDesktopCommand: async (userId, deviceId, commandPayload, chatId) => {
        try {
            if (!userId) throw new Error("User ID required");
            if (!deviceId) throw new Error("Device ID required for targeted routing");

            const queueRef = collection(db, 'users', userId, 'devices', deviceId, 'queue');

            let data = {};
            if (typeof commandPayload === 'string') {
                data = { command: commandPayload };
            } else {
                data = commandPayload;
            }

            const docRef = await addDoc(queueRef, {
                ...data,
                status: 'pending',
                response: null,
                chatId: chatId || null,
                createdAt: serverTimestamp()
            });

            return docRef.id;
        } catch (error) {
            console.error("Error sending desktop command:", error);
            throw error;
        }
    },

    /**
     * Listens to a specific task ID for completion.
     * @param {string} userId 
     * @param {string} deviceId
     * @param {string} taskId 
     * @param {function} onComplete - Callback(data)
     * @returns {function} unsubscribe
     */
    subscribeToTask: (userId, deviceId, taskId, onComplete) => {
        if (!userId || !deviceId) return () => { };
        const taskRef = doc(db, 'users', userId, 'devices', deviceId, 'queue', taskId);
        return onSnapshot(taskRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.status === 'completed') {
                    onComplete(data.response);
                } else if (data.status === 'error') {
                    onComplete(`Error: ${data.response}`);
                }
            }
        });
    },

    /**
     * Creates a folder on a remote device and returns the created path.
     * @param {string} userId - The user's UID
     * @param {string} deviceId - The target device UUID
     * @param {string} folderName - Name of the folder to create
     * @param {string} parentPath - Parent directory (defaults to ~/Projects on agent)
     * @returns {Promise<string>} - The full path of the created folder
     */
    createFolder: async (userId, deviceId, folderName, parentPath = '') => {
        const taskId = await DesktopService.sendDesktopCommand(userId, deviceId, {
            command: 'create_folder',
            folderName,
            parentPath
        });

        // Return a promise that resolves when the agent completes the task
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                unsubscribe();
                reject(new Error('Folder creation timed out'));
            }, 15000);

            const unsubscribe = DesktopService.subscribeToTask(userId, deviceId, taskId, (response) => {
                clearTimeout(timeout);
                unsubscribe();
                if (response && !response.startsWith('Error')) {
                    resolve(response);
                } else {
                    reject(new Error(response || 'Unknown error'));
                }
            });
        });
    },

    /**
     * Sends a command to the agent to quit the application.
     * @param {string} userId - The user's UID
     * @param {string} deviceId - The target device UUID
     */
    shutdownAgent: async (userId, deviceId) => {
        return DesktopService.sendDesktopCommand(userId, deviceId, {
            command: 'quit_app'
        });
    }
};

